(ns storrito.editor.music-modal.search
  "The search view of the music modal, as pure Replicant: search the
   Instagram catalog or browse a category, with a floating player bar to
   preview songs and a round button that advances the loaded song to the
   section picker. Reads its state from `[:ui/modal :music]` and emits
   `:music/*` data actions handled in ui.editor2.modal-music."
  (:require [storrito.editor.music-modal.preview :as preview]
            [storrito.ui.button :as button]
            [storrito.ui.spinner :as spinner]
            [clojure.string :as str]))

(def browse-categories
  "The browse categories of the Instagram app's music sticker, in the
   app's tab order (see musicBrowse.php)."
  [["for-you" "For you"]
   ["trending" "Trending"]
   ["saved" "Saved"]
   ["original-audio" "Original audio"]])

(defn connected-instagram-users
  "The [username instagram-user-id] pairs of the connected Instagram
   accounts, sorted by username (seeded into the music state from the
   :query/ig-account-data query result at modal open)."
  [music]
  (map (fn [{:keys [instagram.user/name instagram.user/id]}]
         [name (str id)])
       (get-in music [:instagram-data :instagram.users])))

(defn selected-account-id
  "The Instagram account that fetches the music: the one picked in the
   dropdown, or the first connected account."
  [music]
  (or (:account-id music)
      (second (first (connected-instagram-users music)))))

(defn account-select
  "Selects the connected Instagram account that fetches the music. The
   available songs and the For you category depend on the used account."
  [music]
  (let [users (connected-instagram-users music)]
    (when (seq users)
      [:div.flex.items-center.gap-2
       [:label.text-sm.text-gray-600
        {:for "music-account-select"}
        "Account:"]
       [:select#music-account-select.bg-white.border-gray-400.rounded.text-gray-800.text-sm.shadow-sm
        {:value (selected-account-id music)
         :disabled (:searching? music)
         :on {:change [[:store/assoc-in [:ui/modal :music :account-id]
                        :event/target.value]]}}
        (for [[username id] users]
          [:option {:value id
                    :replicant/key id}
           (str "@" username)])]])))

(defn category-pills
  "One pill per browse category, like the tabs of the Instagram app's
   music sticker. Clicking a pill fetches that category's tracks."
  [music]
  [:div.flex.flex-wrap.gap-2.mt-3
   (for [[category label] browse-categories]
     (let [active? (= category
                      (:category (:tracks-request music)))]
       [:button.px-3.py-1.rounded-full.text-sm.font-medium.border
        {:type "button"
         :replicant/key category
         :class (if active?
                  preview/accent-solid
                  "bg-white text-gray-800 border-gray-300 hover:bg-gray-100")
         :disabled (:searching? music)
         :on {:click [[:music/load-tracks {:category category}]]}}
        label]))])

(defn player-bar
  "The fixed player row at the bottom of the modal that controls the
   preview playback of the loaded song: cover, title, artist, a
   play/pause button and a round button that selects the song for the
   section picker (the next step)."
  [music]
  (let [track (:player-track music)
        playing? (get-in music [:preview :playing?])]
    [:div.flex.items-center.gap-3.rounded-xl.bg-gray-200.shadow-lg.px-3.py-2
     (preview/cover-thumbnail track 44)
     [:div.flex-1.min-w-0
      [:div.font-semibold.truncate (:title track)]
      [:div.text-sm.text-gray-600.truncate (:display_artist track)]]
     [:button.text-2xl.text-gray-700.hover:text-gray-900.w-9.flex.justify-center
      {:type "button"
       :title (if playing? "Pause" "Play")
       :on {:click [[:music/toggle-playback]]}}
      (if playing?
        [:i.fas.fa-pause]
        [:i.fas.fa-play])]
     [:button.rounded-full.w-9.h-9.flex.items-center.justify-center
      {:type "button"
       :class preview/accent-round
       :title "Select this song"
       :on {:click [[:music/select-track track]]}}
      [:i.fas.fa-arrow-right]]]))

(defn track-list
  [music]
  (let [tracks (:tracks music)
        player-id (:id (:player-track music))
        request (:tracks-request music)
        ;; A playlist (category) load shows its progress here in the
        ;; table; a song search shows it on the search button instead:
        category-loading? (and (:searching? music)
                               (some? (:category request)))]
    ;; Explicit max-height so the list scrolls on its own; the bottom
    ;; padding keeps the last song scrollable into view above the
    ;; floating player bar:
    [:div.overflow-y-auto.pb-24
     {:style {:max-height "50vh"}}
     (if category-loading?
       [:div.flex.justify-center.items-center.py-16.text-gray-500
        (spinner/vire-spinner-sm)]
       [:table.w-full
        [:tbody
         (for [track tracks
               :let [active? (= (:id track) player-id)]]
           [:tr.cursor-pointer
            {:replicant/key (:id track)
             :class (if active?
                      "bg-gray-200"
                      "hover:bg-gray-100")
             :on {:click [[:music/load-player-track track]]}}
            [:td.py-1.px-2
             (preview/cover-thumbnail track 40)]
            [:td.py-1.px-2
             [:div.flex.items-center
              (when (preview/preview-playing? music track)
                (preview/playing-indicator))
              [:span.font-bold
               (:title track)]
              (when (:is_explicit track)
                [:span.ml-1.px-1.text-xs.text-white.bg-gray-500.rounded "E"])]
             [:div.text-sm.text-gray-500
              (str (:display_artist track)
                   (when (:duration_in_ms track)
                     (str " • "
                          (preview/format-duration (:duration_in_ms track)))))]]])]])]))

(defn panel
  [state]
  (let [music (get-in state [:ui/modal :music])
        dispatch! (:replicant/dispatch! state)
        searching? (:searching? music)
        request (:tracks-request music)
        song-search? (and searching?
                          (some? (:query request)))]
    [:div.mt-4
     [:div
      [:form.m-0
       {:on {:submit (fn [e]
                       (.preventDefault e)
                       (dispatch! {:actions [[:music/load-tracks {:query? true}]]}))}}
       [:div
        [:label.text-gray-800.block.text-base.font-bold.mb-2 "Search a Song:"]
        [:div.flex
         ;; Disabled only while a playlist loads (its progress shows in
         ;; the table); a song search keeps the field usable:
         [:input.mr-2.max-w-full.w-full.bg-white.border-gray-400.rounded.text-gray-800.shadow-sm.focus:hover:border-gray-500.focus:shadow-md.disabled:bg-gray-100
          {:type "text"
           :autoComplete "off"
           :auto-focus true
           :id "music-search-text"
           :value (:query-text music "")
           :disabled (and searching?
                          (some? (:category request)))
           :on {:input [[:store/assoc-in [:ui/modal :music :query-text]
                         :event/target.value]]}}]
         (button/button-primary
           {:class "ml-2"
            :disabled (or (str/blank? (:query-text music))
                          searching?)}
           (if song-search?
             (list [:span.mr-2 (spinner/vire-spinner-sm)]
                   [:span "Searching..."])
             (list [:span.mr-2 [:i.fas.fa-search]]
                   [:span "Search"])))]]
       (category-pills music)
       (when-let [error-message (:error-message music)]
         [:div.font-medium.text-sm.mt-2.text-red-600
          error-message])]]

     ;; The list scrolls inside this area (explicit max-height, so it
     ;; does not depend on the flex height of the modal ancestors), and
     ;; the player floats over its bottom (the list's bottom padding
     ;; keeps the last song reachable above it):
     [:div.relative.mt-2
      (track-list music)
      (when (:player-track music)
        [:div.absolute.bottom-3.left-2.right-2
         (player-bar music)])]
     [:p.text-xs.text-gray-500.mt-1
      "The available songs depend on the connected Instagram account."]]))
